import { defineStore } from 'pinia';
import { IProductStore } from '@/storePinia/stores/product/interfaces/IProductStore';
import { IProductSize, IProductWeight } from '@/storePinia/interfaces/IProduct/IProduct';
import { replaceUrl } from '@/utils/location';
import useLoader from '@use/useLoader/useLoader';
import axios from 'axios';
import { useResultsStore } from '@/storePinia/stores/results';
import { IBreadcrumbs } from '@/interfaces';
import { useStoresStore } from '@/storePinia/stores/stores';
import { isProductAvailable, isProductOnOrder } from '@/utils/product/product';
import { IProductDetail } from '@/storePinia/stores/product/interfaces/IProductDetail';

const loader = useLoader();

interface IProductStoreData extends IProductStore {
  isPairSelected: boolean;
}

type IProductStoreGetters = {
  id(state: IProductStore): string | number | undefined;
  activeSize(state: IProductStore): IProductSize | undefined;
  isAvailable(state: IProductStore): (payload?: IProductStore['product']) => boolean;
  onOrder(state: IProductStore): (payload?: IProductStore['product']) => boolean;
  activeWeight(state: IProductStore): IProductWeight | undefined;
  canBuy(state: IProductStoreData): boolean;
};

type IProductStoreActions = {
  getPageData: (payload: {
    url: string;
    changeUrl?: boolean;
  }) => Promise<IProductStore | undefined>;
  updatePageData: (payload: { sku: string; size: string; quantity: number }) => Promise<boolean>;
  changeQuantity: (payload?: {
    url?: string;
    quantity?: number;
  }) => Promise<IProductStore | undefined>;
  setPrice: (payload: { price: number }) => void;
  setData: (data: IProductStore) => void;
};

export const useProductStore = defineStore<
  'product',
  IProductStoreData,
  IProductStoreGetters,
  IProductStoreActions
>('product', {
  state: (): IProductStoreData => ({
    dataLayer: {},
    isPairSelected: false,
    messages: {},
    product: {} as IProductDetail,
    stores: {},
    kits: {},
  }),
  getters: {
    id: (state) => state.product?.offer?.id || state.product?.id,
    activeSize: (state) => {
      const SIZES = state.product?.skuMatrix?.SIZES?.items;

      if (SIZES) {
        return SIZES?.find((el) => el.active);
      }

      return undefined;
    },
    isAvailable: (state) => (payload) => {
      const product = payload ?? state.product;
      return isProductAvailable(product);
    },
    onOrder: (state) => (payload) => {
      const product = payload ?? state.product;
      return isProductOnOrder(product);
    },
    activeWeight(state) {
      return state.product.weights?.items?.find((el) => el.active);
    },
    canBuy: (state) =>
      // pair product weight. pair isn't selected
      !(state.product?.isPair && !state.isPairSelected),
  },
  actions: {
    async getPageData({ url, changeUrl }) {
      try {
        loader.start();

        interface IResponseData extends IProductStore {
          breadcrumbs: IBreadcrumbs;
        }

        const { data, status } = await axios.get<IResponseData>(url, { params: { api: 'y' } });

        if (status !== 200) {
          throw new Error(`bad request response ${url}?api=y \n status: ${status}`);
        }

        if (data.breadcrumbs) {
          const resultsStore = useResultsStore();
          resultsStore.$patch({ breadcrumbs: data.breadcrumbs });
        }

        if (data.stores) {
          const storesStore = useStoresStore();
          storesStore.$patch(data.stores);
        }

        this.setData(data);

        if (changeUrl) {
          replaceUrl(url);
        }

        return data;
      } catch (err) {
        console.error(err);
        return undefined;
      } finally {
        loader.stop();
      }
    },
    async updatePageData(payload) {
      try {
        if (this.product.detailPage) {
          loader.start();

          const params = { api: 'y', ...payload };

          const { data, status } = await axios.get(this.product.detailPage, {
            params: params,
          });

          if (status !== 200) {
            throw new Error(`bad request response ${payload}?api=y \n status: ${status}`);
          }

          this.$patch(data);

          return true;
        }
        return false;
      } catch (err) {
        console.error(err);
        return false;
      } finally {
        loader.stop();
      }
    },
    async changeQuantity(payload) {
      try {
        const url = payload?.url || this.product?.detailPage;
        const quantity = payload?.quantity;

        if (url && quantity) {
          loader.start();

          const { data: responseData, status } = await axios.get(url, {
            params: { api: 'y', quantity },
          });

          if (status === 200) {
            this.$patch(responseData);
          }

          return responseData;
        }
        return undefined;
      } catch (err) {
        console.error(err);
        return undefined;
      } finally {
        loader.stop();
      }
    },
    setPrice({ price }) {
      if (!this.product.price) return;

      const number = new Intl.NumberFormat('uk-UA', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price);

      if (this.product.price.eurRate) {
        const priceEUR = (price / +this.product.price.eurRate).toFixed(2);
        this.product.price.printPrice = `UAH ${number}`;
        this.product.price.priceEUR = +priceEUR;
        this.product.price.printPriceEUR = `EUR ~ ${priceEUR}`;
      } else {
        this.product.price.printPrice = `${number} грн.`;
      }

      this.product.price.price = price;
    },
    setData(data: IProductStore) {
      this.$state = {
        ...data,
        isPairSelected: this.isPairSelected,
      };
    },
  },
});
